// FacultyTimetable.jsx
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import ViewFacultyTimetable from "./ViewFacultyTimetable.jsx";
import FacultyTimetableImport from "./FacultyTimetableImport";

const FacultyTimetable = ({ branch: lockedBranch }) => {
  const [selectedTab, setSelectedTab] = useState("view");
  const [faculties, setFaculties] = useState([]);
  const [filteredFaculties, setFilteredFaculties] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [timetable, setTimetable] = useState({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
  });
  const [originalTimetable, setOriginalTimetable] = useState(null); // Store for reset
  const [loading, setLoading] = useState(false);
  const [clashErrors, setClashErrors] = useState([]);
  const [facultyInfo, setFacultyInfo] = useState(null);
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  const [selectedPeriodForSubstitution, setSelectedPeriodForSubstitution] = useState(null);
  const [availableFaculty, setAvailableFaculty] = useState([]);
  const [selectedSubstituteFaculty, setSelectedSubstituteFaculty] = useState("");
  const [loadingAvailableFaculty, setLoadingAvailableFaculty] = useState(false);
  const [substitutionHistory, setSubstitutionHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Dynamic Section Cache by (branch_semester)
  const [sectionsCache, setSectionsCache] = useState({});

  const fetchSectionsForBranchAndSemester = async (b, sem) => {
    if (!b || !sem) return;
    const cacheKey = `${b.trim()}_${sem}`;
    if (sectionsCache[cacheKey]) return;

    try {
      const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, {
        params: { branch: b, semester: sem }
      });
      if (res.data.success && res.data.sections?.length > 0) {
        setSectionsCache(prev => ({
          ...prev,
          [cacheKey]: res.data.sections
        }));
      }
    } catch (err) {
      console.error(`Error fetching sections for ${b} Sem ${sem}:`, err);
    }
  };

  const getSectionsForPeriod = (b, sem) => {
    if (!b || !sem) return ["A", "B", "C", "D"];
    const cacheKey = `${b.trim()}_${sem}`;
    if (!sectionsCache[cacheKey]) {
      fetchSectionsForBranchAndSemester(b, sem);
      return ["A", "B", "C", "D"];
    }
    return sectionsCache[cacheKey];
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const timeSlots = [
    '9:00am - 10:00am',
    '10:00am - 10:50am',
    '10:50am - 11:00am',
    '11:00am - 11:50am',
    '11:50am - 12:40pm',
    '12:40pm - 1:30pm',
    '1:30pm - 2:20pm',
    '2:20pm - 3:10pm',
    '3:10pm - 4:00pm'
  ];

  useEffect(() => {
    getFacultyData();
    getSubjectData();
    getBranchData();
  }, []);

  useEffect(() => {
    if (lockedBranch) {
      setFilteredFaculties(faculties.filter(f => f.department === lockedBranch));
    } else {
      setFilteredFaculties(faculties);
    }
  }, [faculties, lockedBranch]);

  useEffect(() => {
    if (selectedFaculty) {
      loadFacultyInfo();
      loadFacultyTimetable();
      loadSubstitutionHistory();
    }
  }, [selectedFaculty]);

  const getFacultyData = () => {
    setLoading(true);
    axios
      .get(`${baseApiURL()}/faculty/details/getDetails2`)
      .then((res) => {
        if (res.data.success) {
          setFaculties(res.data.faculties);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to fetch faculty data");
      })
      .finally(() => setLoading(false));
  };

  const getSubjectData = () => {
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((res) => {
        if (res.data.success) {
          console.log("Loaded subjects:", res.data.subject);
          setSubjects(res.data.subject);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to fetch subjects");
      });
  };

  const getBranchData = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((res) => {
        if (res.data.success) {
          setBranches(res.data.branches);
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to fetch branches");
      });
  };

  const loadFacultyInfo = async () => {
    try {
      const response = await axios.post(`${baseApiURL()}/faculty/details/getDetails`, { 
        employeeId: selectedFaculty 
      });

      if (response.data.success && response.data.user[0]) {
        const faculty = response.data.user[0];
        setFacultyInfo({
          name: `${faculty.firstName} ${faculty.middleName ? faculty.middleName + " " : ""}${faculty.lastName}`,
          department: faculty.department,
          employeeId: faculty.employeeId
        });
      }
    } catch (error) {
      console.error("Error loading faculty info:", error);
    }
  };

  const loadSubstitutionHistory = async () => {
    try {
      const response = await axios.get(
        `${baseApiURL()}/faculty/details/substitution-history/${selectedFaculty}`
      );
      if (response.data.success) {
        setSubstitutionHistory(response.data.substitutions);
      }
    } catch (error) {
      console.error("Error loading substitution history:", error);
    }
  };

  // Function to get filtered subjects for a specific branch, semester and regulation
  const getFilteredSubjects = (branch, semester, regulation) => {
    if (!branch || !semester) {
      return subjects; // Return all subjects if no basic filter
    }
    
    const filtered = subjects.filter(
      (subject) => 
        subject.semester === parseInt(semester) && 
        subject.branch?.name === branch &&
        (!regulation || subject.regulation?.toUpperCase() === regulation.toUpperCase())
    );
    
    return filtered;
  };

  const loadFacultyTimetable = () => {
    setLoading(true);
    axios
      .post(`${baseApiURL()}/faculty/details/getDetails`, { employeeId: selectedFaculty })
      .then((res) => {
        if (res.data.success && res.data.user[0]?.timetable) {
          const newTimetable = {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
          };

          res.data.user[0].timetable.forEach(dayData => {
            if (dayData.day && dayData.periods) {
              newTimetable[dayData.day] = dayData.periods;
              
              // Auto-fetch missing regulations for existing periods
              dayData.periods.forEach((period, idx) => {
                if (!period.regulation && period.branch && period.semester && period.section) {
                  fetchRegulationForPeriod(dayData.day, idx, period.branch, period.semester, period.section);
                }
              });
            }
          });

          setTimetable(newTimetable);
          setOriginalTimetable(JSON.parse(JSON.stringify(newTimetable))); // Store deep copy for reset
        } else {
          const emptyTimetable = {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
          };
          setTimetable(emptyTimetable);
          setOriginalTimetable(JSON.parse(JSON.stringify(emptyTimetable)));
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load faculty timetable");
      })
      .finally(() => setLoading(false));
  };

  const addPeriod = (day) => {
    setTimetable(prev => {
      const currentDaySchedule = prev[day] || [];
      return {
        ...prev,
        [day]: [...currentDaySchedule, {
          periodNumber: currentDaySchedule.length + 1,
          subject: "",
          branch: "",
          semester: "",
          section: "",
          startTime: "",
          endTime: "",
          regulation: ""
        }]
      };
    });
  };

  const removePeriod = (day, index) => {
    setTimetable(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index).map((period, i) => ({
        ...period,
        periodNumber: i + 1
      }))
    }));
  };

  const fetchRegulationForPeriod = (day, index, branch, semester, section) => {
    if (branch && semester && section) {
      axios
        .post(`${baseApiURL()}/student/details/getDetails`, {
          branch,
          semester,
          section,
        })
        .then((res) => {
          if (res.data.success && res.data.user.length > 0) {
            const detectedRegulation = res.data.user[0].regulation;
            if (detectedRegulation) {
              setTimetable(prev => {
                const daySchedule = [...prev[day]];
                daySchedule[index] = { 
                  ...daySchedule[index], 
                  regulation: detectedRegulation.toUpperCase() 
                };
                return { ...prev, [day]: daySchedule };
              });
            }
          }
        })
        .catch((err) => console.error("Error fetching regulation:", err));
    }
  };

  const updatePeriod = (day, index, field, value) => {
    setTimetable(prev => {
      const daySchedule = prev[day] ? [...prev[day]] : [];
      if (!daySchedule[index]) return prev;

      const updatedPeriod = {
        ...daySchedule[index],
        [field]: value
      };
      
      // If branch, semester or regulation changes, clear the subject if it's not available for the new combination
      if ((field === 'branch' || field === 'semester' || field === 'regulation') && updatedPeriod.subject) {
        const isSpecialPeriod = ["Break", "Sports", "Library", "Other"].includes(updatedPeriod.subject);
        if (!isSpecialPeriod) {
          const filteredSubjects = getFilteredSubjects(updatedPeriod.branch, updatedPeriod.semester, updatedPeriod.regulation);
          const subjectExists = filteredSubjects.some(subj => subj.name === updatedPeriod.subject);
          if (!subjectExists) {
            updatedPeriod.subject = "";
          }
        }
      }
      
      // If branch, semester or section changes, trigger regulation fetch
      if (field === 'branch' || field === 'semester' || field === 'section') {
        fetchRegulationForPeriod(day, index, updatedPeriod.branch, updatedPeriod.semester, updatedPeriod.section);
      }

      // Handle standard slot selection
      if (field === 'standardSlot' && value) {
        const [start, end] = value.split(' - ');
        updatedPeriod.startTime = start;
        updatedPeriod.endTime = end;
      }
      
      daySchedule[index] = updatedPeriod;
      return {
        ...prev,
        [day]: daySchedule
      };
    });
  };

  // Function to check if a period has a clash with existing faculty timetables
  const checkPeriodClash = (period, day) => {
    if (!period.branch || !period.semester || !period.section || !period.startTime || !period.endTime) {
      return null;
    }

    // Skip special periods
    if (["Break", "Sports", "Library", "Other"].includes(period.subject)) {
      return null;
    }

    // Check against clash errors
    return clashErrors.find(clash => 
      clash.day === day &&
      clash.time === `${period.startTime} - ${period.endTime}` &&
      clash.semester === period.semester &&
      clash.section === period.section &&
      clash.branch === period.branch
    );
  };

  const saveFacultyTimetable = () => {
    if (!selectedFaculty) {
      toast.error("Please select a faculty member");
      return;
    }

    // Clear previous clash errors
    setClashErrors([]);

    // Convert to backend format
    const timetableToSave = Object.entries(timetable)
      .map(([day, periods]) => ({
        day,
        periods: periods // Don't filter here, catch missing fields in validation below
      }))
      .filter(dayData => dayData.periods.length > 0);

    // Validate all required fields
    const hasEmptyFields = timetableToSave.some(dayData => 
      dayData.periods.some(period => {
        // For special periods, only subject, startTime and endTime are required
        const isSpecialPeriod = ["Break", "Sports", "Library", "Other"].includes(period.subject);
        
        if (isSpecialPeriod) {
          return !period.subject || !period.startTime || !period.endTime;
        } else {
          return !period.subject || 
                 !period.branch || 
                 !period.semester || 
                 !period.section || 
                 !period.startTime || 
                 !period.endTime;
        }
      })
    );

    if (hasEmptyFields) {
      toast.error("Please fill all required fields for all active periods");
      return;
    }

    toast.loading("Saving Faculty Timetable");
    axios
      .put(`${baseApiURL()}/faculty/details/updateTimetable/${selectedFaculty}`, { 
        timetable: timetableToSave 
      })
      .then((res) => {
        toast.dismiss();
        if (res.data.success) {
          toast.success("Faculty timetable updated successfully");
          // Reload the timetable to ensure consistency
          loadFacultyTimetable();
          // Switch back to view mode
          setSelectedTab("view");
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        toast.dismiss();
        console.error(err); 
        
        // Handle clash validation errors
        if (err.response?.data?.clashes && err.response.data.clashes.length > 0) {
          setClashErrors(err.response.data.clashes);
          toast.error("Faculty timetable clash detected! Please check the conflicts below.");
        } else {
          toast.error(err.response?.data?.message || "Failed to save faculty timetable");
        }
      });
  };

  // Open substitution modal
  const openSubstitutionModal = (day, period) => {
    setSelectedPeriodForSubstitution({ day, ...period });
    setShowSubstitutionModal(true);
    setSelectedSubstituteFaculty("");
    fetchAvailableFaculty(day, period);
  };

  // Fetch available faculty for substitution
  const fetchAvailableFaculty = async (day, period) => {
    if (!period.startTime || !period.endTime) {
      toast.error("Period must have start and end time");
      return;
    }

    setLoadingAvailableFaculty(true);
    try {
      const response = await axios.get(
        `${baseApiURL()}/faculty/details/available-for-substitution`,
        {
          params: {
            day,
            startTime: period.startTime,
            endTime: period.endTime,
            branch: period.branch,
            semester: period.semester,
            section: period.section,
            currentFacultyId: selectedFaculty
          }
        }
      );

      if (response.data.success) {
        setAvailableFaculty(response.data.availableFaculty);
        if (response.data.availableFaculty.length === 0) {
          toast.error("No faculty available at this time");
        }
      }
    } catch (error) {
      console.error("Error fetching available faculty:", error);
      toast.error("Failed to fetch available faculty");
    } finally {
      setLoadingAvailableFaculty(false);
    }
  };

  // Perform substitution
  const performSubstitution = async () => {
    if (!selectedSubstituteFaculty) {
      toast.error("Please select a faculty member");
      return;
    }

    if (!selectedPeriodForSubstitution) return;

    toast.loading("Performing substitution...");

    try {
      const response = await axios.post(
        `${baseApiURL()}/faculty/details/substitute`,
        {
          originalFacultyId: selectedFaculty,
          substituteFacultyId: selectedSubstituteFaculty,
          day: selectedPeriodForSubstitution.day,
          periodNumber: selectedPeriodForSubstitution.periodNumber,
          startTime: selectedPeriodForSubstitution.startTime,
          endTime: selectedPeriodForSubstitution.endTime,
          subject: selectedPeriodForSubstitution.subject,
          branch: selectedPeriodForSubstitution.branch,
          semester: selectedPeriodForSubstitution.semester,
          section: selectedPeriodForSubstitution.section,
          originalTimetable: originalTimetable
        }
      );

      toast.dismiss();

      if (response.data.success) {
        toast.success("Faculty substituted successfully!");
        setShowSubstitutionModal(false);
        setSelectedPeriodForSubstitution(null);
        loadFacultyTimetable(); // Reload both faculties' timetables
        loadSubstitutionHistory();
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error performing substitution:", error);
      
      if (error.response?.data?.clashes) {
        toast.error("Substitute faculty has a clash at this time");
      } else {
        toast.error(error.response?.data?.message || "Failed to perform substitution");
      }
    }
  };

  // Reset timetable to original state
  const resetTimetable = async () => {
    if (!originalTimetable) {
      toast.error("No original timetable found");
      return;
    }

    if (!window.confirm("Are you sure you want to reset the timetable to its original state? This will undo all substitutions.")) {
      return;
    }

    toast.loading("Resetting timetable...");

    try {
      const response = await axios.post(
        `${baseApiURL()}/faculty/details/reset-timetable`,
        {
          facultyId: selectedFaculty,
          originalTimetable: originalTimetable
        }
      );

      toast.dismiss();

      if (response.data.success) {
        toast.success("Timetable reset successfully!");
        loadFacultyTimetable(); // Reload timetable
        loadSubstitutionHistory();
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error resetting timetable:", error);
      toast.error("Failed to reset timetable");
    }
  };

  const handleImportSuccess = () => {
    toast.success("Timetable imported successfully!");
    setSelectedTab("view");
    loadFacultyTimetable();
  };

  if (loading && !faculties.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Faculty Selection - Common for all tabs */}
      <div className="bento-card p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <label htmlFor="faculty" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
              Select Faculty
            </label>
            <select
              id="faculty"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white font-semibold text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
              value={selectedFaculty}
              onChange={(e) => setSelectedFaculty(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Select Faculty --</option>
              {filteredFaculties.map((faculty) => (
                <option key={faculty._id} value={faculty.employeeId}>
                  {faculty.firstName} {faculty.middleName} {faculty.lastName} ({faculty.employeeId}) - {faculty.department}
                </option>
              ))}
            </select>
          </div>
          
          {/* Faculty Info Display */}
          {facultyInfo && (
            <div className="bg-indigo-50/80 dark:bg-indigo-950/40 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-500 dark:text-slate-400 font-medium">Name:</span>
                  <p className="font-bold text-slate-900 dark:text-white">{facultyInfo.name}</p>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 font-medium">Department:</span>
                  <p className="font-bold text-slate-900 dark:text-white">{facultyInfo.department}</p>
                </div>
              </div>
            </div>
          )}

          {/* History Button */}
          {selectedFaculty && substitutionHistory.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowHistoryModal(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition shadow-xs flex items-center gap-2 cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View History ({substitutionHistory.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bento-card p-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 max-w-xs mx-auto rounded-2xl">
        <div className="grid grid-cols-2 gap-1">
          {["view", "edit"].map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
                selectedTab === tab
                  ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
              onClick={() => setSelectedTab(tab)}
              disabled={!selectedFaculty && tab !== "view"}
            >
              {tab === "view" ? "View Timetable" : "Edit Timetable"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="w-full">
        {/* No Faculty Selected Message */}
        {!selectedFaculty && (
          <div className="bento-card p-12 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs">
            <svg className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">No Faculty Selected</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
              Please select a faculty member from the dropdown above to continue.
            </p>
          </div>
        )}

        {/* View Tab */}
        {selectedTab === "view" && selectedFaculty && (
          <ViewFacultyTimetable facultyId={selectedFaculty} />
        )}

        {/* Edit Tab */}
        {selectedTab === "edit" && selectedFaculty && (
          <div className="space-y-6">

            {/* Clash Error Display */}
            {clashErrors.length > 0 && (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-2xl p-5 shadow-xs">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-rose-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="text-base font-bold text-rose-900 dark:text-rose-300">Faculty Timetable Clash Detected</h3>
                </div>
                <p className="text-rose-700 dark:text-rose-400 text-xs mb-3">The following conflicts were found. Please resolve them before saving:</p>
                <div className="space-y-2.5 max-h-96 overflow-y-auto">
                  {clashErrors.map((clash, index) => (
                    <div key={index} className="bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-800 rounded-xl p-3.5 text-xs text-slate-800 dark:text-slate-200">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-bold text-slate-900 dark:text-white">{clash.day}</span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-600 dark:text-slate-400 font-semibold">{clash.time}</span>
                      </div>
                      <div className="text-slate-700 dark:text-slate-300">
                        Conflict: {clash.branch} - Sem {clash.semester} - Sec {clash.section}
                      </div>
                      <div className="text-rose-600 dark:text-rose-400 font-bold mt-0.5">
                        Conflicts with: {clash.conflictingFaculty.name} ({clash.conflictingFaculty.employeeId}) - {clash.conflictingFaculty.subject}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => setClashErrors([])}
                    className="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {daysOfWeek.map((day) => (
                <div key={day} className="bento-card p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">{day}</h3>
                    <button
                      onClick={() => addPeriod(day)}
                      className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
                    >
                      + Period
                    </button>
                  </div>
                  
                  {timetable[day].length === 0 ? (
                    <div className="text-center py-6 text-xs text-slate-400 dark:text-slate-500">
                      No periods scheduled for {day}
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {timetable[day].map((period, index) => {
                        const clash = checkPeriodClash(period, day);
                        return (
                        <div key={index} className={`border rounded-xl p-3.5 space-y-2.5 transition-all ${
                          clash 
                            ? 'border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40' 
                            : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950'
                        }`}>
                          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Period {period.periodNumber}</span>
                            <div className="flex items-center space-x-1.5">
                              {period.subject && !["Break", "Sports", "Library", "Other"].includes(period.subject) && (
                                <button
                                  onClick={() => openSubstitutionModal(day, period)}
                                  className="p-1 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer"
                                  title="Substitute Faculty"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => removePeriod(day, index)}
                                className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          <div className="space-y-2 text-xs">
                            {/* Subject Dropdown */}
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Subject</label>
                              <select
                                className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                value={period.subject || ""}
                                onChange={(e) => updatePeriod(day, index, "subject", e.target.value)}
                              >
                                <option value="">Select Subject</option>
                                <option value="Break">☕ Break</option>
                                <option value="Sports">🏀 Sports</option>
                                <option value="Library">📚 Library</option>
                                <option value="Other">⚙️ Other</option>
                                {period.branch && period.semester ? (
                                  getFilteredSubjects(period.branch, period.semester, period.regulation).map((subj) => (
                                    <option key={subj._id} value={subj.name}>
                                      {subj.name} ({subj.code})
                                    </option>
                                  ))
                                ) : (
                                  <option value="" disabled>Select branch and semester first</option>
                                )}
                              </select>
                            </div>

                            {/* Branch */}
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Branch</label>
                              <select
                                className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                value={period.branch || ""}
                                onChange={(e) => updatePeriod(day, index, "branch", e.target.value)}
                                disabled={["Break", "Sports", "Library", "Other"].includes(period.subject)}
                              >
                                <option value="">Select Branch</option>
                                {branches.map((branch) => (
                                  <option key={branch._id} value={branch.name}>{branch.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              {/* Semester */}
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Semester</label>
                                <select
                                  className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                  value={period.semester || ""}
                                  onChange={(e) => updatePeriod(day, index, "semester", e.target.value)}
                                  disabled={["Break", "Sports", "Library", "Other"].includes(period.subject)}
                                >
                                  <option value="">Semester</option>
                                  {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                                    <option key={sem} value={sem}>{sem}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Section */}
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Section</label>
                                <select
                                  className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                  value={period.section || ""}
                                  onChange={(e) => updatePeriod(day, index, "section", e.target.value)}
                                  disabled={["Break", "Sports", "Library", "Other"].includes(period.subject)}
                                >
                                  <option value="">Section</option>
                                  {getSectionsForPeriod(period.branch, period.semester).map((sec) => (
                                    <option key={sec} value={sec}>{sec}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Time Inputs */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Start Time</label>
                                <input
                                  type="text"
                                  placeholder="e.g. 9:00am"
                                  className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                  value={period.startTime || ""}
                                  onChange={(e) => updatePeriod(day, index, "startTime", e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">End Time</label>
                                <input
                                  type="text"
                                  placeholder="e.g. 10:00am"
                                  className="w-full p-2 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                  value={period.endTime || ""}
                                  onChange={(e) => updatePeriod(day, index, "endTime", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <button
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition shadow-xs cursor-pointer"
                onClick={saveFacultyTimetable}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Faculty Timetable'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Substitution Modal */}
      {showSubstitutionModal && selectedPeriodForSubstitution && (
        <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Substitute Faculty</h3>
              <button
                onClick={() => {
                  setShowSubstitutionModal(false);
                  setSelectedPeriodForSubstitution(null);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                ✕
              </button>
            </div>

            {/* Period Details */}
            <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 text-xs space-y-1 text-slate-700 dark:text-slate-300">
              <p><span className="font-bold">Day:</span> {selectedPeriodForSubstitution.day} | <span className="font-bold">Period:</span> {selectedPeriodForSubstitution.periodNumber}</p>
              <p><span className="font-bold">Subject:</span> {selectedPeriodForSubstitution.subject}</p>
              <p><span className="font-bold">Time:</span> {selectedPeriodForSubstitution.startTime} - {selectedPeriodForSubstitution.endTime}</p>
              <p><span className="font-bold">Class:</span> {selectedPeriodForSubstitution.branch} - Sem {selectedPeriodForSubstitution.semester} - Sec {selectedPeriodForSubstitution.section}</p>
            </div>

            {/* Available Faculty Dropdown */}
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Select Substitute Faculty
              </label>
              {loadingAvailableFaculty ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <select
                  className="w-full p-2.5 border border-slate-200 dark:border-slate-800 rounded-xl text-xs bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  value={selectedSubstituteFaculty}
                  onChange={(e) => setSelectedSubstituteFaculty(e.target.value)}
                >
                  <option value="">Select Faculty</option>
                  {availableFaculty.map((faculty) => (
                    <option key={faculty.employeeId} value={faculty.employeeId}>
                      {faculty.name} ({faculty.employeeId}) - {faculty.department}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => {
                  setShowSubstitutionModal(false);
                  setSelectedPeriodForSubstitution(null);
                }}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={performSubstitution}
                disabled={!selectedSubstituteFaculty || loadingAvailableFaculty}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition shadow-xs disabled:opacity-50 cursor-pointer"
              >
                Confirm Substitution
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Substitution History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-w-xl w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Substitution History</h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                ✕
              </button>
            </div>

            {substitutionHistory.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-6">No substitution history found</p>
            ) : (
              <div className="space-y-3">
                {substitutionHistory.map((sub, index) => (
                  <div key={index} className="border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-xl p-3.5 text-xs text-slate-700 dark:text-slate-300">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-slate-900 dark:text-white">{sub.day} - Period {sub.periodNumber}</span>
                      <span className="text-[11px] text-slate-500">{new Date(sub.substitutionDate).toLocaleDateString()}</span>
                    </div>
                    <p><span className="font-bold">Subject:</span> {sub.subject} ({sub.startTime} - {sub.endTime})</p>
                    <p><span className="font-bold">Class:</span> {sub.branch} - Sem {sub.semester} - Sec {sub.section}</p>
                    <p className="text-indigo-600 dark:text-indigo-400 font-bold mt-0.5">Substituted From: {sub.substitutedFrom}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyTimetable;